import React, { useMemo, useEffect } from 'react';
import { ExpectedImpactWidget } from './ExpectedImpactWidget';
import { useQuery } from '@apollo/client';
import { useAtomValue } from 'jotai';
import { GET_EXPECTED_IMPACT } from '../../../../graphql/queries/portfolio';
import {
  latestSnapshotIdAtom,
  triggerHardRefetchAtom,
} from '~/atoms/dashboardAtom';
import { getMetricDescriptionWithFormula } from '~/utils/metricsMapping';

interface ExpectedImpactData {
  performance_impact: {
    total_expected_improvement?: string;
    breakdown: {
      risk_reduction?: string;
      current_expected_return?: number;
      proposed_expected_return?: number;
      current_risk_annual?: number;
      proposed_risk_annual?: number;
    };
  };
  performance?: {
    // Forward-looking risk metrics (ex-ante)
    current_ex_ante_cvar_pct?: number;
    proposed_ex_ante_cvar_pct?: number;
    cvar_improvement_pct?: number;
    current_shortfall_prob_pct?: number;
    proposed_shortfall_prob_pct?: number;
    shortfall_prob_improvement_pct?: number;
    current_forward_drawdown_pct?: number;
    proposed_forward_drawdown_pct?: number;
    forward_drawdown_improvement_pct?: number;
    drawdown_budget_utilization_pct?: number;
  };
  current?: {
    beta?: number;
    tracking_error?: number;
  };
  proposed?: {
    beta?: number;
    tracking_error?: number;
  };
}

interface ExpectedImpactResponse {
  EQPortfoliosAnalysis: {
    analysis_data: ExpectedImpactData;
  }[];
}

export interface ExpectedImpactWidgetWrapperProps {
  widgetId: string;
  handleRemoveWidget?: (widgetId: string) => void;
  shortDescription?: string;
}

export const ExpectedImpactWidgetWrapper: React.FC<
  ExpectedImpactWidgetWrapperProps
> = ({ widgetId, handleRemoveWidget, shortDescription }) => {
  const latestSnapshotId = useAtomValue(latestSnapshotIdAtom);
  const triggerHardRefetch = useAtomValue(triggerHardRefetchAtom);
  const snapshotId = latestSnapshotId;

  const { data, loading, error, refetch } = useQuery<ExpectedImpactResponse>(
    GET_EXPECTED_IMPACT,
    {
      variables: { snapshotId },
      notifyOnNetworkStatusChange: true,
      skip: !snapshotId,
    },
  );
  const impactData = useMemo(() => {
    if (loading || !data?.EQPortfoliosAnalysis?.[0]?.analysis_data) {
      return [];
    }

    const analysisData = data.EQPortfoliosAnalysis[0].analysis_data;
    const impact = analysisData.performance_impact;
    const breakdown = impact?.breakdown;
    const performance = analysisData.performance;
    const metrics = [];

    // Expected Return metrics (EWMA)
    if (
      breakdown?.current_expected_return !== undefined &&
      breakdown?.proposed_expected_return !== undefined
    ) {
      const currentReturn = Number(breakdown.current_expected_return);
      const proposedReturn = Number(breakdown.proposed_expected_return);
      const improvement = proposedReturn - currentReturn;

      const returnLabel = 'Expected Annual Return (EWMA)';
      metrics.push({
        label: returnLabel,
        description: getMetricDescriptionWithFormula(returnLabel),
        currentValue: !isNaN(currentReturn)
          ? `${(currentReturn * 100).toFixed(1)}%`
          : 'N/A',
        proposedValue: !isNaN(proposedReturn)
          ? `${(proposedReturn * 100).toFixed(1)}%`
          : 'N/A',
        value: !isNaN(improvement)
          ? `${improvement > 0 ? '+' : ''}${(improvement * 100).toFixed(1)}%`
          : 'N/A',
        isPositive: !isNaN(improvement) ? improvement > 0 : false,
      });
    } else if (impact?.total_expected_improvement) {
      // Fallback to old structure
      const totalImprovement = parseFloat(impact.total_expected_improvement);
      metrics.push({
        label: 'Expected Annual Return Improvement',
        value: !isNaN(totalImprovement)
          ? `${totalImprovement > 0 ? '+' : ''}${totalImprovement.toFixed(1)}%`
          : 'N/A',
        isPositive: !isNaN(totalImprovement) ? totalImprovement > 0 : false,
      });
    }

    // Risk metrics
    if (
      breakdown?.current_risk_annual !== undefined &&
      breakdown?.proposed_risk_annual !== undefined
    ) {
      const currentRisk = Number(breakdown.current_risk_annual);
      const proposedRisk = Number(breakdown.proposed_risk_annual);
      const riskChange = proposedRisk - currentRisk;

      const riskLabel = 'Annual Risk (Ledoit-Wolf)';
      metrics.push({
        label: riskLabel,
        description: getMetricDescriptionWithFormula(riskLabel),
        currentValue: !isNaN(currentRisk)
          ? `${(currentRisk * 100).toFixed(1)}%`
          : 'N/A',
        proposedValue: !isNaN(proposedRisk)
          ? `${(proposedRisk * 100).toFixed(1)}%`
          : 'N/A',
        value: !isNaN(riskChange)
          ? `${riskChange > 0 ? '+' : ''}${(riskChange * 100).toFixed(1)}%`
          : 'N/A',
        isPositive: !isNaN(riskChange) ? riskChange < 0 : false, // Lower risk is positive
      });
    } else if (breakdown?.risk_reduction) {
      // Fallback to old structure
      const riskReduction = parseFloat(breakdown.risk_reduction);
      metrics.push({
        label: 'Risk Reduction',
        value: !isNaN(riskReduction)
          ? `${riskReduction > 0 ? '+' : ''}${riskReduction.toFixed(1)}%`
          : 'N/A',
        isPositive: !isNaN(riskReduction) ? riskReduction > 0 : false,
      });
    }

    // Forward-looking risk metrics (ex-ante)
    if (performance) {
      // Ex-ante CVaR (Conditional Value at Risk)
      if (
        performance.current_ex_ante_cvar_pct !== undefined &&
        performance.proposed_ex_ante_cvar_pct !== undefined
      ) {
        const currentCVar = Number(performance.current_ex_ante_cvar_pct);
        const proposedCVar = Number(performance.proposed_ex_ante_cvar_pct);
        const cvarImprovement = Number(performance.cvar_improvement_pct || 0);

        const cvarLabel = 'Ex-Ante CVaR (95%, 21-day)';
        metrics.push({
          label: cvarLabel,
          description: getMetricDescriptionWithFormula(cvarLabel),
          currentValue: !isNaN(currentCVar)
            ? `${currentCVar.toFixed(1)}%`
            : 'N/A',
          proposedValue: !isNaN(proposedCVar)
            ? `${proposedCVar.toFixed(1)}%`
            : 'N/A',
          value: !isNaN(cvarImprovement)
            ? `${cvarImprovement > 0 ? '+' : ''}${cvarImprovement.toFixed(1)}%`
            : 'N/A',
          isPositive: !isNaN(cvarImprovement) ? cvarImprovement < 0 : false, // Lower CVaR is better
        });
      }

      // Shortfall Probability
      if (
        performance.current_shortfall_prob_pct !== undefined &&
        performance.proposed_shortfall_prob_pct !== undefined
      ) {
        const currentShortfall = Number(performance.current_shortfall_prob_pct);
        const proposedShortfall = Number(
          performance.proposed_shortfall_prob_pct,
        );
        const shortfallImprovement = Number(
          performance.shortfall_prob_improvement_pct || 0,
        );

        const shortfallLabel = 'Shortfall Probability (vs 0%)';
        metrics.push({
          label: shortfallLabel,
          description: getMetricDescriptionWithFormula(shortfallLabel),
          currentValue: !isNaN(currentShortfall)
            ? `${currentShortfall.toFixed(1)}%`
            : 'N/A',
          proposedValue: !isNaN(proposedShortfall)
            ? `${proposedShortfall.toFixed(1)}%`
            : 'N/A',
          value: !isNaN(shortfallImprovement)
            ? `${shortfallImprovement > 0 ? '+' : ''}${shortfallImprovement.toFixed(1)}%`
            : 'N/A',
          isPositive: !isNaN(shortfallImprovement)
            ? shortfallImprovement < 0
            : false, // Lower shortfall probability is better
        });
      }

      // Forward Drawdown Forecast
      if (
        performance.current_forward_drawdown_pct !== undefined &&
        performance.proposed_forward_drawdown_pct !== undefined
      ) {
        const currentDD = Number(performance.current_forward_drawdown_pct);
        const proposedDD = Number(performance.proposed_forward_drawdown_pct);
        const ddImprovement = Number(
          performance.forward_drawdown_improvement_pct || 0,
        );

        const drawdownLabel = 'Forward Max Drawdown Forecast';
        metrics.push({
          label: drawdownLabel,
          description: getMetricDescriptionWithFormula(drawdownLabel),
          currentValue: !isNaN(currentDD) ? `${currentDD.toFixed(1)}%` : 'N/A',
          proposedValue: !isNaN(proposedDD)
            ? `${proposedDD.toFixed(1)}%`
            : 'N/A',
          value: !isNaN(ddImprovement)
            ? `${ddImprovement > 0 ? '+' : ''}${ddImprovement.toFixed(1)}%`
            : 'N/A',
          isPositive: !isNaN(ddImprovement) ? ddImprovement < 0 : false, // Lower drawdown is better
        });
      }

      // Drawdown Budget Utilization
      if (performance.drawdown_budget_utilization_pct !== undefined) {
        const budgetUtilization = Number(
          performance.drawdown_budget_utilization_pct,
        );

        const budgetLabel = 'Drawdown Budget Utilization';
        metrics.push({
          label: budgetLabel,
          description: getMetricDescriptionWithFormula(budgetLabel),
          value: !isNaN(budgetUtilization)
            ? `${budgetUtilization.toFixed(1)}%`
            : 'N/A',
          isPositive: !isNaN(budgetUtilization)
            ? budgetUtilization <= 80
            : false, // Under 80% utilization is considered good
        });
      }
    }

    // Additional portfolio metrics (beta, tracking error, etc.)
    const current = analysisData.current;
    const proposed = analysisData.proposed;

    if (current && proposed) {
      // Beta
      if (current.beta !== undefined && proposed.beta !== undefined) {
        const currentBeta = Number(current.beta);
        const proposedBeta = Number(proposed.beta);
        const betaChange = proposedBeta - currentBeta;

        const betaLabel = 'Portfolio Beta';
        metrics.push({
          label: betaLabel,
          description: getMetricDescriptionWithFormula(betaLabel),
          currentValue: !isNaN(currentBeta) ? currentBeta.toFixed(2) : 'N/A',
          proposedValue: !isNaN(proposedBeta) ? proposedBeta.toFixed(2) : 'N/A',
          value: !isNaN(betaChange)
            ? `${betaChange > 0 ? '+' : ''}${betaChange.toFixed(2)}`
            : 'N/A',
          isPositive:
            !isNaN(currentBeta) && !isNaN(proposedBeta)
              ? Math.abs(proposedBeta - 1.0) < Math.abs(currentBeta - 1.0)
              : false, // Closer to 1.0 is typically better for tracking
        });
      }

      // Tracking Error
      if (
        current.tracking_error !== undefined &&
        proposed.tracking_error !== undefined
      ) {
        const currentTE = Number(current.tracking_error);
        const proposedTE = Number(proposed.tracking_error);
        const teChange = proposedTE - currentTE;

        const trackingErrorLabel = 'Tracking Error';
        metrics.push({
          label: trackingErrorLabel,
          description: getMetricDescriptionWithFormula(trackingErrorLabel),
          currentValue: !isNaN(currentTE) ? `${currentTE.toFixed(1)}%` : 'N/A',
          proposedValue: !isNaN(proposedTE)
            ? `${proposedTE.toFixed(1)}%`
            : 'N/A',
          value: !isNaN(teChange)
            ? `${teChange > 0 ? '+' : ''}${teChange.toFixed(1)}%`
            : 'N/A',
          isPositive: false, // Neutral - depends on strategy
        });
      }
    }

    return metrics;
  }, [data, loading]);

  useEffect(() => {
    if (snapshotId) {
      refetch();
    }
  }, [snapshotId, refetch]);

  // Force hard refetch when triggered by other widgets
  useEffect(() => {
    if (triggerHardRefetch > 0) {
      refetch();
    }
  }, [triggerHardRefetch, refetch]);

  return (
    <ExpectedImpactWidget
      data={impactData}
      isLoading={loading}
      error={error || null}
      refetch={refetch}
      widgetId={widgetId}
      handleRemoveWidget={handleRemoveWidget}
      title={shortDescription || 'Expected Impact'}
      snapshotId={snapshotId}
    />
  );
};
